import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate } from 'react-router-dom';
import FloatingAlerts from '../../../shared/ui/floating-alerts/floating-alerts.jsx';

const DASHBOARD_VIEW_STORAGE_KEY = 'admin_dashboard_view_mode';
const DASHBOARD_VIEW_MODES = Object.freeze({
  compact: 'compact',
  detailed: 'detailed',
});

function getInitialDashboardViewMode() {
  if (typeof window === 'undefined') {
    return DASHBOARD_VIEW_MODES.compact;
  }

  try {
    const stored = window.localStorage.getItem(DASHBOARD_VIEW_STORAGE_KEY);
    return stored === DASHBOARD_VIEW_MODES.detailed ? DASHBOARD_VIEW_MODES.detailed : DASHBOARD_VIEW_MODES.compact;
  } catch {
    return DASHBOARD_VIEW_MODES.compact;
  }
}

function RecentRecordRow({ row }) {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const openRow = () => {
    if (row.to) {
      navigate(row.to);
    }
  };

  return (
    <tr
      className={row.to ? 'app-clickable-row app-dashboard-recent-table-row' : 'app-dashboard-recent-table-row'}
      tabIndex={row.to ? 0 : undefined}
      onClick={openRow}
      onKeyDown={(event) => {
        if (event.key === 'Enter' || event.key === ' ') {
          event.preventDefault();
          openRow();
        }
      }}
    >
      <td>
        <div className="app-dashboard-recent-row-main">
          <span className="app-dashboard-recent-row-title">{row.title}</span>
          {row.meta ? <span className="app-dashboard-recent-row-meta">{row.meta}</span> : null}
        </div>
      </td>
      {row.fields.map((field) => (
        <td key={field.label}>
          <span className="app-dashboard-recent-field-label d-md-none">{t(field.label)}</span>
          {field.badgeClass ? (
            <span className={`badge rounded-pill fw-semibold text-uppercase ${field.badgeClass}`}>
              {field.value || '-'}
            </span>
          ) : (
            <span className={`app-dashboard-recent-field-value ${field.valueClass || ''}`.trim()}>
              {field.value || '-'}
            </span>
          )}
        </td>
      ))}
    </tr>
  );
}

function RecentRecordsTable({ section, isLoading }) {
  const { t } = useTranslation();
  const fields = section.rows[0]?.fields || [];
  const columnCount = 1 + fields.length;

  return (
    <div className="table-responsive">
      <table className="table search-table align-middle mb-0 app-dashboard-recent-table">
        <thead className="header-item">
          <tr>
            <th>{t('common.item')}</th>
            {fields.map((field) => (
              <th key={field.label}>{t(field.label)}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {isLoading && section.rows.length === 0 ? (
            <tr>
              <td colSpan={columnCount} className="app-dashboard-recent-empty">
                {t('common.loading')}
              </td>
            </tr>
          ) : null}
          {!isLoading && section.rows.length === 0 ? (
            <tr>
              <td colSpan={columnCount} className="app-dashboard-recent-empty">
                {t(section.emptyLabel)}
              </td>
            </tr>
          ) : null}
          {section.rows.map((row) => (
            <RecentRecordRow key={row.key} row={row} />
          ))}
        </tbody>
      </table>
    </div>
  );
}

function RecentRecordDetailedRow({ row }) {
  const { t } = useTranslation();

  return (
    <Link to={row.to || '#'} className="app-dashboard-recent-detail-row text-decoration-none">
      <div className="app-dashboard-recent-row-main">
        <span className="app-dashboard-recent-row-title">{row.title}</span>
        {row.meta ? <span className="app-dashboard-recent-row-meta">{row.meta}</span> : null}
      </div>
      <div className="app-dashboard-recent-detail-fields">
        {row.fields.map((field) => (
          <div key={field.label} className="app-dashboard-recent-detail-field">
            <span className="app-dashboard-recent-field-label">{t(field.label)}</span>
            {field.badgeClass ? (
              <span className={`badge rounded-pill fw-semibold text-uppercase ${field.badgeClass}`}>
                {field.value || '-'}
              </span>
            ) : (
              <span className={`app-dashboard-recent-field-value ${field.valueClass || ''}`.trim()}>
                {field.value || '-'}
              </span>
            )}
          </div>
        ))}
      </div>
    </Link>
  );
}

function RecentRecordsDetailedList({ section, isLoading }) {
  const { t } = useTranslation();

  return (
    <div className="app-dashboard-recent-detail-list">
      {isLoading && section.rows.length === 0 ? (
        <div className="app-dashboard-recent-detail-empty">{t('common.loading')}</div>
      ) : null}
      {!isLoading && section.rows.length === 0 ? (
        <div className="app-dashboard-recent-detail-empty">{t(section.emptyLabel)}</div>
      ) : null}
      {section.rows.map((row) => (
        <RecentRecordDetailedRow key={row.key} row={row} />
      ))}
    </div>
  );
}

function RecentRecordsCard({ section, isLoading, viewMode }) {
  const { t } = useTranslation();
  const isDetailed = viewMode === DASHBOARD_VIEW_MODES.detailed;

  return (
    <section className="card app-dashboard-recent-card">
      <div className="card-body">
        <div className="app-dashboard-recent-card-header">
          <div className="d-flex align-items-center gap-2 min-w-0">
            <span className="app-dashboard-recent-card-icon">
              <i className={section.iconClass} aria-hidden="true"></i>
            </span>
            <h3 className="app-dashboard-recent-card-title">{t(section.title)}</h3>
          </div>
          <Link
            to={section.to}
            className="btn btn-sm btn-light app-dashboard-recent-card-link"
            aria-label={t('dashboard.openFullList', { list: t(section.title) })}
          >
            <i className="ti ti-link" aria-hidden="true"></i>
          </Link>
        </div>
        {isDetailed ? (
          <RecentRecordsDetailedList section={section} isLoading={isLoading} />
        ) : (
          <RecentRecordsTable section={section} isLoading={isLoading} />
        )}
      </div>
    </section>
  );
}

function DashboardViewToggle({ value, onChange }) {
  const { t } = useTranslation();

  return (
    <div className="btn-group app-dashboard-view-toggle" role="group" aria-label={t('dashboard.viewMode')}>
      <button
        type="button"
        className={`btn btn-sm ${value === DASHBOARD_VIEW_MODES.compact ? 'btn-primary' : 'btn-outline-primary'}`}
        onClick={() => onChange(DASHBOARD_VIEW_MODES.compact)}
      >
        {t('dashboard.compactView')}
      </button>
      <button
        type="button"
        className={`btn btn-sm ${value === DASHBOARD_VIEW_MODES.detailed ? 'btn-primary' : 'btn-outline-primary'}`}
        onClick={() => onChange(DASHBOARD_VIEW_MODES.detailed)}
      >
        {t('dashboard.detailedView')}
      </button>
    </div>
  );
}

function DashboardPageView({
  isLoading,
  error,
  cards,
  recentSections = [],
  isRecentLoading = false,
}) {
  const { t } = useTranslation();
  const [viewMode, setViewMode] = useState(getInitialDashboardViewMode);
  const isCompact = viewMode === DASHBOARD_VIEW_MODES.compact;

  useEffect(() => {
    try {
      window.localStorage.setItem(DASHBOARD_VIEW_STORAGE_KEY, viewMode);
    } catch {
      // ignore unavailable storage
    }
  }, [viewMode]);

  return (
    <div>
      <FloatingAlerts error={error} />
      <div className="app-page-header d-flex align-items-start justify-content-between gap-3 flex-wrap">
        <div>
          <h2>{t('dashboard.title')}</h2>
          <p>{t('dashboard.subtitle')}</p>
        </div>
        <DashboardViewToggle value={viewMode} onChange={setViewMode} />
      </div>

      <div className={isCompact ? 'app-dashboard-stat-compact-strip' : 'app-dashboard-stat-grid'}>
        {cards.map((card) => (
          <div key={card.key} className={isCompact ? 'app-dashboard-stat-compact-item' : 'app-dashboard-stat-grid-item'}>
            {isCompact ? (
              <Link
                to={card.to || '#'}
                className={`app-dashboard-stat-compact-icon text-white ${card.toneClass}`}
                aria-label={`${t(card.label)}: ${isLoading ? '...' : card.value}`}
                title={t(card.label)}
              >
                <i className={`${card.iconClass} fs-6`} aria-hidden="true"></i>
                <span className="app-dashboard-stat-compact-badge">{isLoading ? '...' : card.value}</span>
              </Link>
            ) : (
              <Link
                to={card.to || '#'}
                className="card app-dashboard-stat-card text-decoration-none"
                aria-label={`${t(card.label)}: ${isLoading ? '...' : card.value}`}
              >
                <div className="card-body p-9">
                  <div className="hstack gap-9">
                    <div
                      className={`app-dashboard-stat-card-icon round-56 rounded-circle text-white d-flex align-items-center justify-content-center ${card.toneClass}`}
                    >
                      <i className={`${card.iconClass} fs-6`} aria-hidden="true"></i>
                    </div>
                    <div className="app-dashboard-stat-card-content align-self-center">
                      <h3 className="mb-1 fs-6">{isLoading ? '...' : card.value}</h3>
                      <span className="text-muted">{t(card.label)}</span>
                    </div>
                  </div>
                </div>
              </Link>
            )}
          </div>
        ))}
      </div>

      <div className="app-dashboard-recent-grid">
        {recentSections.map((section) => (
          <RecentRecordsCard key={section.key} section={section} isLoading={isRecentLoading} viewMode={viewMode} />
        ))}
      </div>
    </div>
  );
}

export default DashboardPageView;
